import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Building2, User, Mail, Phone, MapPin, Lock, Check, 
  ArrowRight, ArrowLeft, Sparkles, Shield, Users, 
  Calendar, FileText, Loader2
} from 'lucide-react';

export default function TenantOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null);
  
  const [formData, setFormData] = useState({
    // Business Info
    business_name: '',
    business_phone: '',
    business_address: '',
    business_city: '',
    business_state: '',
    business_zip: '',
    // Owner Info
    owner_first_name: '',
    owner_last_name: '',
    owner_email: '',
    owner_password: '',
    owner_password_confirm: '',
    owner_phone: ''
  });

  const [validation, setValidation] = useState({
    emailAvailable: null,
    businessAvailable: null,
    checkingEmail: false,
    checkingBusiness: false
  });

  useEffect(() => {
    fetchPlans();
    
    // Check for referral code from URL first (for direct /register?ref=CODE links)
    const urlRefCode = searchParams.get('ref');
    if (urlRefCode) {
      const code = urlRefCode.toUpperCase();
      localStorage.setItem('referral_code', code);
      setReferralCode(code);
      validateReferralCode(code);
    } else {
      // Fallback to localStorage (set by LandingPage or LoginPage)
      const storedRefCode = localStorage.getItem('referral_code');
      if (storedRefCode) {
        setReferralCode(storedRefCode);
        validateReferralCode(storedRefCode);
      }
    }
  }, [searchParams]);

  const fetchPlans = async () => {
    try {
      const response = await api.get('/onboarding/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const validateReferralCode = async (code) => {
    if (!code) {
      setReferralValid(null);
      return;
    }
    try {
      const response = await api.get(`/referrals/validate/${code}`);
      setReferralValid(response.data);
    } catch (error) {
      setReferralValid({ valid: false, message: 'Could not validate code' });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const checkEmailAvailability = async () => {
    if (!formData.owner_email || !formData.owner_email.includes('@')) return;
    
    setValidation(prev => ({ ...prev, checkingEmail: true }));
    try {
      const response = await api.get(`/onboarding/check-email/${encodeURIComponent(formData.owner_email)}`);
      setValidation(prev => ({ 
        ...prev, 
        emailAvailable: response.data.available,
        checkingEmail: false 
      }));
    } catch (error) {
      setValidation(prev => ({ ...prev, checkingEmail: false }));
    }
  };

  const checkBusinessAvailability = async () => {
    if (!formData.business_name || formData.business_name.length < 3) return;
    
    setValidation(prev => ({ ...prev, checkingBusiness: true }));
    try {
      const response = await api.get(`/onboarding/check-business/${encodeURIComponent(formData.business_name)}`);
      setValidation(prev => ({ 
        ...prev, 
        businessAvailable: response.data.available,
        checkingBusiness: false 
      }));
    } catch (error) {
      setValidation(prev => ({ ...prev, checkingBusiness: false }));
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return selectedPlan !== '';
      case 2:
        return formData.business_name.length >= 3 && 
               validation.businessAvailable !== false;
      case 3:
        return formData.owner_first_name && 
               formData.owner_last_name && 
               formData.owner_email.includes('@') &&
               formData.owner_password.length >= 8 &&
               formData.owner_password === formData.owner_password_confirm &&
               validation.emailAvailable !== false;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/onboarding/register', {
        ...formData,
        subscription_plan: selectedPlan,
        referral_code: referralCode || null  // Include referral code in registration
      });

      if (response.data.success) {
        // Clear the referral code from localStorage after successful registration
        localStorage.removeItem('referral_code');
        
        toast.success(response.data.message);
        // Redirect to login after successful registration
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Registration successful! Please login with your new account.',
              email: formData.owner_email 
            }
          });
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Choose Your Plan</h2>
        <p className="text-gray-600 mt-2">Select the plan that best fits your business needs</p>
      </div>

      {/* Referral Code Banner */}
      {referralCode && referralValid && (
        <div className={`p-4 rounded-lg border ${referralValid.valid ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="referral-banner">
          {referralValid.valid ? (
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Referral Code Applied!</p>
                <p className="text-sm text-green-600">Referred by: {referralValid.referrer_name}</p>
              </div>
            </div>
          ) : (
            <p className="text-amber-700 text-sm">{referralValid.message || 'Invalid referral code'}</p>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            className={`p-6 cursor-pointer transition-all ${
              selectedPlan === plan.id 
                ? 'ring-2 ring-purple-500 bg-purple-50' 
                : 'hover:border-purple-300'
            } ${plan.recommended ? 'relative' : ''}`}
            onClick={() => setSelectedPlan(plan.id)}
            data-testid={`plan-${plan.id}`}
          >
            {plan.recommended && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full">
                  Recommended
                </span>
              </div>
            )}
            
            <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
            <div className="mt-2">
              <span className="text-3xl font-bold text-purple-600">${plan.price_monthly}</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              or ${plan.price_yearly}/year (save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
            </p>
            
            <ul className="mt-4 space-y-2">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            {selectedPlan === plan.id && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-center gap-2 text-purple-600">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Selected</span>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Business Information</h2>
        <p className="text-gray-600 mt-2">Tell us about your home care business</p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="business_name">Business Name *</Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="business_name"
              name="business_name"
              value={formData.business_name}
              onChange={handleInputChange}
              onBlur={checkBusinessAvailability}
              placeholder="Your Home Care Agency Name"
              className="pl-10"
              data-testid="business-name-input"
            />
          </div>
          {validation.checkingBusiness && (
            <p className="text-sm text-gray-500 mt-1">Checking availability...</p>
          )}
          {validation.businessAvailable === false && (
            <p className="text-sm text-red-500 mt-1">This business name is already registered</p>
          )}
          {validation.businessAvailable === true && (
            <p className="text-sm text-green-500 mt-1">Business name is available</p>
          )}
        </div>

        <div>
          <Label htmlFor="business_phone">Business Phone</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="business_phone"
              name="business_phone"
              value={formData.business_phone}
              onChange={handleInputChange}
              placeholder="(555) 555-5555"
              className="pl-10"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="business_address">Address</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="business_address"
              name="business_address"
              value={formData.business_address}
              onChange={handleInputChange}
              placeholder="123 Main Street"
              className="pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label htmlFor="business_city">City</Label>
            <Input
              id="business_city"
              name="business_city"
              value={formData.business_city}
              onChange={handleInputChange}
              placeholder="Charlotte"
            />
          </div>
          <div>
            <Label htmlFor="business_state">State</Label>
            <Input
              id="business_state"
              name="business_state"
              value={formData.business_state}
              onChange={handleInputChange}
              placeholder="NC"
            />
          </div>
          <div>
            <Label htmlFor="business_zip">ZIP</Label>
            <Input
              id="business_zip"
              name="business_zip"
              value={formData.business_zip}
              onChange={handleInputChange}
              placeholder="28202"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 max-w-xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Create Your Account</h2>
        <p className="text-gray-600 mt-2">Set up your administrator account</p>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="owner_first_name">First Name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="owner_first_name"
                name="owner_first_name"
                value={formData.owner_first_name}
                onChange={handleInputChange}
                placeholder="John"
                className="pl-10"
                data-testid="owner-first-name-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="owner_last_name">Last Name *</Label>
            <Input
              id="owner_last_name"
              name="owner_last_name"
              value={formData.owner_last_name}
              onChange={handleInputChange}
              placeholder="Smith"
              data-testid="owner-last-name-input"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="owner_email">Email Address *</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="owner_email"
              name="owner_email"
              type="email"
              value={formData.owner_email}
              onChange={handleInputChange}
              onBlur={checkEmailAvailability}
              placeholder="john@yourbusiness.com"
              className="pl-10"
              data-testid="owner-email-input"
            />
          </div>
          {validation.checkingEmail && (
            <p className="text-sm text-gray-500 mt-1">Checking availability...</p>
          )}
          {validation.emailAvailable === false && (
            <p className="text-sm text-red-500 mt-1">This email is already registered</p>
          )}
          {validation.emailAvailable === true && (
            <p className="text-sm text-green-500 mt-1">Email is available</p>
          )}
        </div>

        <div>
          <Label htmlFor="owner_phone">Phone Number</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="owner_phone"
              name="owner_phone"
              value={formData.owner_phone}
              onChange={handleInputChange}
              placeholder="(555) 555-5555"
              className="pl-10"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="owner_password">Password *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="owner_password"
              name="owner_password"
              type="password"
              value={formData.owner_password}
              onChange={handleInputChange}
              placeholder="Minimum 8 characters"
              className="pl-10"
              data-testid="owner-password-input"
            />
          </div>
          {formData.owner_password && formData.owner_password.length < 8 && (
            <p className="text-sm text-amber-500 mt-1">Password must be at least 8 characters</p>
          )}
        </div>

        <div>
          <Label htmlFor="owner_password_confirm">Confirm Password *</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              id="owner_password_confirm"
              name="owner_password_confirm"
              type="password"
              value={formData.owner_password_confirm}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              className="pl-10"
              data-testid="owner-password-confirm-input"
            />
          </div>
          {formData.owner_password_confirm && formData.owner_password !== formData.owner_password_confirm && (
            <p className="text-sm text-red-500 mt-1">Passwords do not match</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Integrity Home Care</span>
          </div>
          <Button variant="outline" onClick={() => navigate('/login')}>
            Already have an account? Sign In
          </Button>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div 
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= num 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step > num ? <Check className="w-5 h-5" /> : num}
              </div>
              {num < 3 && (
                <div className={`w-24 h-1 mx-2 ${step > num ? 'bg-purple-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-center gap-16 text-sm text-gray-600 mb-8">
          <span className={step === 1 ? 'text-purple-600 font-medium' : ''}>Select Plan</span>
          <span className={step === 2 ? 'text-purple-600 font-medium' : ''}>Business Info</span>
          <span className={step === 3 ? 'text-purple-600 font-medium' : ''}>Create Account</span>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <Card className="p-8">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button
                onClick={() => setStep(step + 1)}
                disabled={!validateStep(step)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !validateStep(3)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="submit-registration-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Complete Registration
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>
      </div>

      {/* Features Section */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Smart Scheduling</h3>
            <p className="text-sm text-gray-600 mt-2">
              Easily manage shifts, assignments, and employee availability
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Digital EMR</h3>
            <p className="text-sm text-gray-600 mt-2">
              Complete electronic medical records with AI-assisted charting
            </p>
          </div>
          <div className="text-center p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Team Management</h3>
            <p className="text-sm text-gray-600 mt-2">
              Training, compliance tracking, and performance monitoring
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
